import { AutocompleteWithLabel } from '@adotel/components'
import { useQuery, useLazyQuery } from '@apollo/client'
import React, { useEffect, useRef, useState } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { ALLOCATION_MISMATCHES_QUERY } from '../../graphql/inventoryAllocateAvailability'
import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import {
  AllocateMismatchesInput,
  AllocationMismatch
} from '../../types/allocateMismatch'
import MismatchTable from './MismatchTable'
import useStyles from './styles/index.styles'

type Props = {}

function MonitoringAllocateMismatch({}: Props) {
  const classes = useStyles()
  const [autocompleteLabelAccommodation, setautocompleteLabelAccommodation] =
    useState<any>()
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [dateObject, setdateObject] = useState<DateObject>({})
  const [input, setinput] = useState<AllocateMismatchesInput>({
    end: '',
    propertyCode: '',
    start: ''
  })

  const { data: accommodationsData, loading: loadingForAccommodationData } =
    useQuery<{ accommodations: Accommodation[] }>(ACCOMMODATIONS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  const [
    getAllocationMismatches,
    { loading: loadingMismatches, data: mismatchesData, error, refetch }
  ] = useLazyQuery<{ allocationMismatches: AllocationMismatch[] }>(
    ALLOCATION_MISMATCHES_QUERY,
    { notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only' }
  )

  const datePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timePickerSelected
      ) {
        setTimePickerSelected(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timePickerSelected])

  useEffect(() => {
    if (input.propertyCode && input.start && input.end) {
      getAllocationMismatches({ variables: { input } })
    }
  }, [input, getAllocationMismatches])

  return (
    <div>
      <div className={classes.filters}>
        <div className={classes.selectAccommodation}>
          <AutocompleteWithLabel
            noHelperText
            value={autocompleteLabelAccommodation}
            label={getTranslate('Accommodation')}
            inputSize="small"
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            options={
              accommodationsData?.accommodations.map((item) => ({
                label: item.basicDetails.name,
                value: item.basicDetails.propertyCode || '',
                code: item.basicDetails.propertyCode || '',
                id: item.basicDetails.accommodationID || ''
              })) || []
            }
            onChange={(_, v: any) => {
              if (v) {
                setautocompleteLabelAccommodation(v)

                setinput({
                  ...input,
                  propertyCode: v.value
                })
              } else {
                setautocompleteLabelAccommodation(null)

                setinput({
                  ...input,
                  propertyCode: ''
                })
              }
            }}
            loading={loadingForAccommodationData}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={[classes.timePicker].join(' ')} ref={datePickerRef}>
          <TimePeriodForm
            isOpen={timePickerSelected}
            setTimePickerSelected={setTimePickerSelected}
            dateObject={dateObject}
            onChange={(data: DateObject) => {
              setdateObject(data)
              setinput({
                ...input,
                start: data.gregorianStartDate || '',
                end: data.gregorianEndDate || ''
              })
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            endDateCanBeSelectAsStartDate
          />
        </div>
      </div>
      {/* Display table once data is loaded */}
      {loadingMismatches && <p>{getTranslate('Loading mismatches ...')}</p>}
      {error && <p>{getTranslate('Error fetching mismatches')}</p>}
      {mismatchesData?.allocationMismatches &&
        mismatchesData.allocationMismatches.length > 0 && (
          <MismatchTable
            data={mismatchesData.allocationMismatches || []}
            loading={loadingMismatches}
            reloadData={refetch}
          />
        )}
      {mismatchesData?.allocationMismatches &&
        mismatchesData.allocationMismatches.length === 0 && (
          <p>{getTranslate('No allocation mismatches found')}</p>
        )}
    </div>
  )
}

export default MonitoringAllocateMismatch
